import {
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { fromP, pFrom } from "@/lib/tool-kit/chem-reactions/acid-base";
import type { WeakConstant } from "@/lib/tool-kit/chem-reactions/acid-base-data";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/**
 * Pick a weak acid or base from a list, or type its constant as K or as pK. Widgets spread
 * `WEAK_CONSTANT_STRINGS` into their own strings and supply the names of the entries.
 */
export const WEAK_CONSTANT_STRINGS = {
  presetCustom: "Enter my own constant",
  formLabel: "Enter the constant as",
  formK: "K",
  formPk: "pK",
  kaLabel: "Ka",
  kbLabel: "Kb",
  pkaLabel: "pKa",
  pkbLabel: "pKb",
  kHint: "At 25 °C. Write K as 1.8e-5 or 1.8 × 10^-5; pK as 4.74.",
} as const;

type ConstantStrings = Record<keyof typeof WEAK_CONSTANT_STRINGS, string>;

export interface ConstantState {
  /** A preset id, or "custom". */
  preset: string;
  form: "k" | "pk";
  text: string;
}

export function initialConstant(first: WeakConstant): ConstantState {
  return { preset: first.id, form: "k", text: first.k.toExponential() };
}

function textFor(k: number, form: "k" | "pk"): string {
  return form === "k" ? k.toExponential() : pFrom(k).toFixed(2);
}

/** K from the state: a preset's exact value, or the typed K or pK; null when unreadable. */
export function readConstant(
  state: ConstantState,
  constants: readonly WeakConstant[]
): number | null {
  const preset = constants.find((entry) => entry.id === state.preset);
  if (preset) {
    return preset.k;
  }
  const value = parseMeasuredNumber(state.text);
  if (value === null) {
    return null;
  }
  const k = state.form === "k" ? value : fromP(value);
  return k > 0 && Number.isFinite(k) ? k : null;
}

export function WeakConstantFields({
  idPrefix,
  side,
  constants,
  names,
  presetLabel,
  state,
  onChange,
  strings,
}: {
  idPrefix: string;
  side: "acid" | "base";
  constants: readonly WeakConstant[];
  names: Record<string, string>;
  presetLabel: string;
  state: ConstantState;
  onChange: (state: ConstantState) => void;
  strings: ConstantStrings;
}) {
  const isAcid = side === "acid";
  let label: string;
  if (state.form === "pk") {
    label = isAcid ? strings.pkaLabel : strings.pkbLabel;
  } else {
    label = isAcid ? strings.kaLabel : strings.kbLabel;
  }
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id={`${idPrefix}-preset`}
          label={presetLabel}
          onChange={(preset) => {
            const found = constants.find((entry) => entry.id === preset);
            onChange({
              ...state,
              preset,
              text: found ? textFor(found.k, state.form) : state.text,
            });
          }}
          options={[
            ...constants.map((entry) => ({
              value: entry.id,
              label: `${names[entry.id] ?? entry.id} (${entry.formula})`,
            })),
            { value: "custom", label: strings.presetCustom },
          ]}
          value={state.preset}
        />
        <TextField
          hint={strings.kHint}
          id={`${idPrefix}-constant`}
          label={label}
          numeric
          onChange={(text) => onChange({ ...state, preset: "custom", text })}
          value={state.text}
        />
      </div>
      <PillGroup
        label={strings.formLabel}
        name={`${idPrefix}-form`}
        onChange={(form) => {
          const found = constants.find((entry) => entry.id === state.preset);
          const k = found ? found.k : readConstant(state, constants);
          onChange({
            ...state,
            form,
            text: k === null ? state.text : textFor(k, form),
          });
        }}
        options={[
          { value: "k", label: strings.formK },
          { value: "pk", label: strings.formPk },
        ]}
        value={state.form}
      />
    </div>
  );
}
